import "./usercss/ask-ai.css";
import { useState, useRef, useEffect } from "react";
import { FiArchive, FiArrowLeft, FiArrowUp, FiSearch, FiMenu, FiTrash2, FiExternalLink } from "react-icons/fi";
import { FaHeadset } from "react-icons/fa6";
import { TbMessagePlus, TbMessages } from "react-icons/tb";
import auriImg from "../../images/auri_full-removebg-whitefont-removebg-preview.png";
import AskHuman from "./AskHuman";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";
import axios from "axios";
import { db, auth } from "../../firebase";
import { collection, addDoc, serverTimestamp, query, where, onSnapshot, orderBy, doc, updateDoc, getDoc, getDocs, limit, deleteDoc, writeBatch } from "firebase/firestore";
import { validateMessage } from "../../utils/profanityFilter";
import ProfanityModal from "../../components/usercomponents/ProfanityModal";

const FUNCTIONS_URL =
    process.env.REACT_APP_FUNCTIONS_URL ||
    "https://us-central1-auravit-bb843.cloudfunctions.net";

export default function AskAIPanel() {
    const { language } = useLanguage();
    const t = translations[language];
    const [activeTab, setActiveTab] = useState("open");
    const [viewMode, setViewMode] = useState("list");
    const [selectedChat, setSelectedChat] = useState(null);
    const [newMessage, setNewMessage] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [chats, setChats] = useState([]);
    const [activeChatId, setActiveChatId] = useState(null);
    const [nextMessageOrder, setNextMessageOrder] = useState(0);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [chatToArchive, setChatToArchive] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [chatToDelete, setChatToDelete] = useState(null);
    const [showOpenModal, setShowOpenModal] = useState(false);
    const [chatToOpen, setChatToOpen] = useState(null);
    const [showProfanityModal, setShowProfanityModal] = useState(false);
    const [chatsLoading, setChatsLoading] = useState(true);
    const [chatsLoaded, setChatsLoaded] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messagesLoaded, setMessagesLoaded] = useState(false);
    const messagesScrollRef = useRef(null);
    const messageOrderRef = useRef(0);
    const itemsPerPage = 5;
    
    // Track current user ID to prevent listener churn on auth changes
    const currentUserIdRef = useRef(null);

    // Message order allocation
    const allocateMessageOrder = () => {
        const order = messageOrderRef.current;
        messageOrderRef.current += 1;
        setNextMessageOrder(messageOrderRef.current);
        return order;
    };

    // Initialize message order ref to start at 1 (first question will be order 1)
    useEffect(() => {
        messageOrderRef.current = 1;
        setNextMessageOrder(1);
    }, []);

    const syncMessageOrderFromMessages = (msgs) => {
        let maxOrder = -1;
        msgs.forEach((msg) => {
            if (msg.order !== undefined && msg.order > maxOrder) {
                maxOrder = msg.order;
            }
        });
        const next = maxOrder + 1;
        messageOrderRef.current = next;
        setNextMessageOrder(next);
    };

    const reserveChatMessageOrder = async (chatId) => {
        // Use local messageOrderRef as primary source of truth to avoid unnecessary Firestore reads
        // Only query Firestore if we suspect desynchronization (rare edge case)
        return allocateMessageOrder();
    };

    // Format timestamp for display
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "Just now";
        
        let date;
        if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else {
            date = new Date(timestamp);
        }
        
        if (isNaN(date.getTime())) return "Just now";
        
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return `Just now`;
        if (diffMins < 60) return `${diffMins} mins`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
        return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    };

    // Create new AI chat document
    const createAIChat = async () => {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) return null;

            const chatRef = await addDoc(collection(db, "users", userId, "aichats"), {
                title: "",
                status: "active",
                createdAt: serverTimestamp(),
                lastUpdated: serverTimestamp()
            });

            return chatRef.id;
        } catch (error) {
            console.error("Error creating AI chat:", error);
            return null;
        }
    };

    // Store message to Firestore with batch write for efficiency
    // Accepts optional `title` and `sourceUrl` to combine updates into same batch
    const storeMessage = async (chatId, { sender, text, order, title, sourceUrl }) => {
        if (!chatId) return;
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) return;
            const batch = writeBatch(db);

            const messagesCollectionRef = collection(db, "users", userId, "aichats", chatId, "messages");
            const newMessageRef = doc(messagesCollectionRef);

            const messageData = { sender, text, order, createdAt: serverTimestamp() };
            if (sourceUrl) messageData.sourceUrl = sourceUrl;
            batch.set(newMessageRef, messageData);

            const chatRef = doc(db, "users", userId, "aichats", chatId);
            const chatUpdates = { lastUpdated: serverTimestamp() };
            if (title !== undefined && title !== null) chatUpdates.title = title;
            batch.update(chatRef, chatUpdates);

            await batch.commit();
        } catch (error) {
            console.error("Error storing message:", error);
        }
    };

    // Update chat title (kept for single-use cases not tied to a message write)
    const updateChatTitle = async (chatId, title) => {
        if (!chatId) return;
        try {
            const userId = auth.currentUser?.uid;
            await updateDoc(doc(db, "users", userId, "aichats", chatId), {
                title,
                lastUpdated: serverTimestamp()
            });
        } catch (error) {
            console.error("Error updating chat title:", error);
        }
    };

    // Load AI chats from Firestore (optimized to prevent listener churn on auth changes)
    useEffect(() => {
        const userId = auth.currentUser?.uid;
        if (!userId) return;
        
        // Only recreate listener if user ID actually changed
        if (currentUserIdRef.current === userId) return;
        currentUserIdRef.current = userId;

        setChatsLoading(true);
        setChatsLoaded(false);

        const chatsQuery = query(
            collection(db, "users", userId, "aichats"),
            orderBy("lastUpdated", "desc")
        );

        const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
            const loadedChats = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    title: data.title || "New Chat",
                    status: data.status || "active",
                    time: formatTimestamp(data.lastUpdated || data.createdAt),
                    lastUpdated: data.lastUpdated,
                    createdAt: data.createdAt
                };
            });
            setChats(prevChats => {
                const hasChanged = loadedChats.length !== prevChats.length || 
                    loadedChats.some((chat, index) => 
                        !prevChats[index] || 
                        chat.id !== prevChats[index].id ||
                        chat.title !== prevChats[index].title ||
                        chat.status !== prevChats[index].status ||
                        chat.lastUpdated !== prevChats[index].lastUpdated
                    );
                return hasChanged ? loadedChats : prevChats;
            });
            setChatsLoading(false);
            setChatsLoaded(true);
        }, (error) => {
            console.error("Error loading AI chats:", error);
            setChatsLoading(false);
            setChatsLoaded(true);
        });

        return () => {
            unsubscribe();
            currentUserIdRef.current = null;
        };
    }, []);

    // Load messages for selected chat
    useEffect(() => {
        if (!activeChatId) return;

        const userId = auth.currentUser?.uid;
        if (!userId) return;

        setMessagesLoading(true);
        setMessagesLoaded(false);

        const messagesRef = collection(db, "users", userId, "aichats", activeChatId, "messages");
        // Limit to last 200 messages to avoid loading entire chat history for long conversations
        // This significantly reduces reads for chats with thousands of messages
        const q = query(
            messagesRef,
            orderBy("order", "asc"),
            orderBy("createdAt", "asc"),
            limit(200)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loadedMessages = [];
            let maxOrder = -1;

            snapshot.forEach(doc => {
                const data = doc.data();
                const messageData = {
                    id: doc.id,
                    sender: data.sender === "assistant" ? "auri" : data.sender,
                    text: data.text,
                    order: data.order,
                    sourceUrl: data.sourceUrl || null,
                    ...data
                };
                console.log('Loaded message from Firestore:', { 
                    originalSender: data.sender, 
                    convertedSender: messageData.sender, 
                    hasSourceUrl: !!messageData.sourceUrl,
                    sourceUrl: messageData.sourceUrl 
                });
                loadedMessages.push(messageData);
                if (data.order !== undefined && data.order > maxOrder) {
                    maxOrder = data.order;
                }
            });

            syncMessageOrderFromMessages(loadedMessages);
            
            // Batch state updates to reduce re-renders
            setDisclaimerConfirmed(true);
            setStarted(true);
            setFinalMessageSent(true);
            setMessages(loadedMessages);
            setMessagesLoading(false);
            setMessagesLoaded(true);
        }, (error) => {
            console.error("Error loading chat messages:", error);
            setMessagesLoading(false);
            setMessagesLoaded(true);
        });

        return () => {
            unsubscribe();
            setMessagesLoading(false);
        };
    }, [activeChatId]);

    // Handle archiving a chat - show confirmation modal
    const handleArchiveChat = (e, chat) => {
        e.stopPropagation();
        setChatToArchive(chat);
        setShowArchiveModal(true);
    };

    // Confirm archive - update Firestore status
    const confirmArchiveChat = async () => {
        if (!chatToArchive) return;

        try {
            const userId = auth.currentUser?.uid;
            await updateDoc(doc(db, "users", userId, "aichats", chatToArchive.id), {
                status: "archived",
                lastUpdated: serverTimestamp()
            });
        } catch (error) {
            console.error("Error archiving chat:", error);
        }

        setShowArchiveModal(false);
        setChatToArchive(null);

        // If the archived chat was selected, deselect it
        if (selectedChat?.id === chatToArchive.id) {
            setSelectedChat(null);
            setActiveChatId(null);
            setStarted(false);
            setDisclaimerConfirmed(false);
            setFinalMessageSent(false);
            messageOrderRef.current = 1;
            setNextMessageOrder(1);
            setMessages([
                {
                    sender: "auri",
                    text: t.heyThereImAuri,
                },
                {
                    sender: "auri",
                    text: t.beforeWeGetStarted,
                },
            ]);
            setNewMessage("");
            setIsUserTyping(false);
            setIsAuriTyping(false);
        }
    };

    // Cancel archive
    const cancelArchiveChat = () => {
        setShowArchiveModal(false);
        setChatToArchive(null);
    };

    // Handle deleting a chat - show confirmation modal
    const handleDeleteChat = (e, chat) => {
        e.stopPropagation();
        setChatToDelete(chat);
        setShowDeleteModal(true);
    };

    // Confirm delete - optimized delete with chunking and progress tracking
    const confirmDeleteChat = async () => {
        if (!chatToDelete) return;

        try {
            const userId = auth.currentUser?.uid;
            if (!userId) return;

            const chatRef = doc(db, "users", userId, "aichats", chatToDelete.id);
            const messagesCollectionRef = collection(db, "users", userId, "aichats", chatToDelete.id, "messages");

            // Safety limit to prevent unbounded operations for extremely large chats
            const MAX_DELETE_ROUNDS = 100; // Prevent infinite loops
            let deleteRounds = 0;
            let hasMore = true;
            let totalDeleted = 0;

            while (hasMore && deleteRounds < MAX_DELETE_ROUNDS) {
                const msgsQuery = query(messagesCollectionRef, limit(400));
                const msgSnap = await getDocs(msgsQuery);
                
                if (msgSnap.empty) {
                    hasMore = false;
                } else {
                    const batch = writeBatch(db);
                    msgSnap.docs.forEach(msgDoc => batch.delete(msgDoc.ref));
                    await batch.commit();
                    totalDeleted += msgSnap.size;
                    
                    if (msgSnap.size < 400) {
                        hasMore = false;
                    }
                }
                deleteRounds++;
            }

            if (deleteRounds >= MAX_DELETE_ROUNDS) {
                console.warn(`Chat deletion reached safety limit after ${totalDeleted} messages. Consider using Cloud Functions for large deletions.`);
            }

            await deleteDoc(chatRef);
        } catch (error) {
            console.error("Error deleting chat:", error);
        }

        setShowDeleteModal(false);
        setChatToDelete(null);

        if (selectedChat?.id === chatToDelete.id) {
            setSelectedChat(null);
            setActiveChatId(null);
            setStarted(false);
            setDisclaimerConfirmed(false);
            setFinalMessageSent(false);
            messageOrderRef.current = 1;
            setNextMessageOrder(1);
            setMessages([
                {
                    sender: "auri",
                    text: t.heyThereImAuri,
                },
                {
                    sender: "auri",
                    text: t.beforeWeGetStarted,
                },
            ]);
            setNewMessage("");
            setIsUserTyping(false);
            setIsAuriTyping(false);
        }
    };

    // Cancel delete
    const cancelDeleteChat = () => {
        setShowDeleteModal(false);
        setChatToDelete(null);
    };

    // Handle opening a chat - show confirmation modal
    const handleOpenChat = (e, chat) => {
        e.stopPropagation();
        setChatToOpen(chat);
        setShowOpenModal(true);
    };

    // Confirm open - update status to active
    const confirmOpenChat = async () => {
        if (!chatToOpen) return;

        try {
            const userId = auth.currentUser?.uid;
            await updateDoc(doc(db, "users", userId, "aichats", chatToOpen.id), {
                status: "active",
                lastUpdated: serverTimestamp()
            });
        } catch (error) {
            console.error("Error opening chat:", error);
        }

        setShowOpenModal(false);
        setChatToOpen(null);
    };

    // Cancel open
    const cancelOpenChat = () => {
        setShowOpenModal(false);
        setChatToOpen(null);
    };


    const filteredChats = chats.filter((chat) =>
        activeTab === "archived" ? chat.status === "archived" : chat.status === "active"
    );

    // Pagination logic
    const totalPages = Math.ceil(filteredChats.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedChats = filteredChats.slice(startIndex, endIndex);

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Reset page when switching tabs
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        
        // Reset chat based on tab
        if (tab === "archived") {
            // Open the latest archived chat
            const archivedChats = chats.filter(chat => chat.archived);
            if (archivedChats.length > 0) {
                const latestArchivedChat = archivedChats[0]; // Should be newest due to sorting
                setSelectedChat(latestArchivedChat);
                setActiveChatId(latestArchivedChat.id);
                setStarted(true);
                setFinalMessageSent(true);
                setDisclaimerConfirmed(true);
                setNewMessage("");
                setIsUserTyping(false);
                setIsAuriTyping(false);
            }
        } else {
            // Open new chat for "open" tab
            setSelectedChat(null);
            setActiveChatId(null);
            setStarted(false);
            setDisclaimerConfirmed(false);
            setFinalMessageSent(false);
            messageOrderRef.current = 1;
            setNextMessageOrder(1);
            setMessages([
                {
                    sender: "auri",
                    text: t.heyThereImAuri,
                },
                {
                    sender: "auri",
                    text: t.beforeWeGetStarted,
                },
            ]);
            setNewMessage("");
            setIsUserTyping(false);
            setIsAuriTyping(false);
        }
    };

    const [isUserTyping, setIsUserTyping] = useState(false);
    const [isAuriTyping, setIsAuriTyping] = useState(false);
    const typingTimeoutRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);


    const handleTyping = (text) => {
        setNewMessage(text);

        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            if (textareaRef.current.scrollHeight) {
                textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
            }
        }

        if (!isUserTyping) setIsUserTyping(true);

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

        typingTimeoutRef.current = setTimeout(() => {
            setIsUserTyping(false);
            typingTimeoutRef.current = null;
        }, 1000);
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;
        
        // Validate message for profanity
        const validation = validateMessage(newMessage);
        if (!validation.isValid) {
            setShowProfanityModal(true);
            return;
        }
        
        const userMessage = newMessage;
        const order = allocateMessageOrder();
        
        let chatId = activeChatId;
        let isNewChat = false;
        if (!chatId) {
            chatId = await createAIChat();
            if (chatId) {
                setActiveChatId(chatId);
                setSelectedChat({ id: chatId, title: "New Chat" });
                isNewChat = true;
            }
        }

        const currentChatObj = chats.find(c => c.id === chatId);
        const chatHasTitle = Boolean(currentChatObj?.title && currentChatObj.title !== "New Chat" && currentChatObj.title !== "");

        setMessages((prev) => [...prev, { sender: "user", text: userMessage, order }]);
        setNewMessage("");
        
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }

        setIsUserTyping(false);
        setIsAuriTyping(true);

        if (chatId) {
            await storeMessage(chatId, { sender: "user", text: userMessage, order });
        }

        try {
            // Build conversation history for Gemini context from the current
            // messages state (last 20 entries = 10 turns). Exclude onboarding
            // bubbles that have no `order` field — those are never persisted.
            const chatHistory = messages
                .filter(m => m.order !== undefined)
                .slice(-20)
                .map(m => ({
                    sender: m.sender === "user" ? "user" : "assistant",
                    text: m.text
                }));

            const response = await axios.post(
                `${FUNCTIONS_URL}/auriChat`,
                {
                    message: userMessage,
                    chatHistory,
                    language: language
                }
            );

            setIsAuriTyping(false);

            console.log('API Response:', response.data);

            if (response.data && response.data.answer) {
                const auriOrder = allocateMessageOrder();
                setMessages((prev) => [...prev, {
                    sender: "auri",
                    text: response.data.answer,
                    order: auriOrder,
                    sourceUrl: response.data.sourceUrl || null
                }]);
                
                if (chatId) {
                    // Use matched FAQ question as title when available, else fall back to user message
                    const titleForBatch = (!chatHasTitle)
                        ? (response.data.faqFound && response.data.faqQuestion
                            ? response.data.faqQuestion
                            : userMessage)
                        : undefined;
                    await storeMessage(chatId, {
                        sender: "assistant",
                        text: response.data.answer,
                        order: auriOrder,
                        title: titleForBatch,
                        ...(response.data.sourceUrl ? { sourceUrl: response.data.sourceUrl } : {})
                    });
                }
            } else {
                const auriOrder = allocateMessageOrder();
                const fallbackText = "Sorry, I couldn't process your question right now. Please try rephrasing or contact a Peer Educator.";
                setMessages((prev) => [...prev, { 
                    sender: "auri", 
                    text: fallbackText,
                    order: auriOrder
                }]);
                
                if (chatId) {
                    const titleForBatch = !chatHasTitle ? userMessage : undefined;
                    await storeMessage(chatId, { 
                        sender: "assistant", 
                        text: fallbackText,
                        order: auriOrder,
                        title: titleForBatch
                    });
                }
            }
        } catch (error) {
            console.error("Error calling Auri chat API:", error);
            setIsAuriTyping(false);
            const auriOrder = allocateMessageOrder();
            const errorText = "Sorry, I'm currently unavailable. Please try again later.";
            setMessages((prev) => [...prev, { 
                sender: "auri", 
                text: errorText,
                order: auriOrder
            }]);
            
            if (chatId) {
                const titleForBatch = !chatHasTitle ? userMessage : undefined;
                await storeMessage(chatId, { 
                    sender: "assistant", 
                    text: errorText,
                    order: auriOrder,
                    title: titleForBatch
                });
            }
        }
    };

    const [messages, setMessages] = useState([
        {
            sender: "auri",
            text: t.heyThereImAuri,
        },
        {
            sender: "auri",
            text: t.beforeWeGetStarted,
        },
    ]);

    const [started, setStarted] = useState(false);
    const [disclaimerConfirmed, setDisclaimerConfirmed] = useState(false);
    const [finalMessageSent, setFinalMessageSent] = useState(false);

    const handleIUnderstand = async () => {
        setDisclaimerConfirmed(true);

        setMessages((prev) => [...prev, { sender: "user", text: t.iUnderstand }]);

        setTimeout(async () => {
            setIsAuriTyping(true);

            setTimeout(async () => {
                setIsAuriTyping(false);
                setMessages((prev) => [
                    ...prev,
                    {
                        sender: "auri",
                        text: t.thanksForConfirming,
                    },
                ]);
                
                // Show Let's Get Started button after Auri's second message is displayed
                setTimeout(() => {
                    setStarted(true);
                }, 100);
            }, 2000);
        }, 850);
    };

    const handleLetsGetStarted = async () => {
        setStarted(true);
        setFinalMessageSent(true);

        setMessages((prev) => [...prev, { sender: "user", text: t.letsGetStarted }]);

        setTimeout(async () => {
            setIsAuriTyping(true);

            setTimeout(async () => {
                setIsAuriTyping(false);
                setMessages((prev) => [
                    ...prev,
                    {
                        sender: "auri",
                        text: t.awesomeLetsDiveIn,
                    },
                ]);
            }, 2000);
        }, 850);
    };

    useEffect(() => {
  if (!messagesScrollRef.current) return;

  requestAnimationFrame(() => {
    const el = messagesScrollRef.current;
    if (el && el.scrollHeight) {
      el.scrollTop = el.scrollHeight + 100;
    }
  });
}, [messages, isAuriTyping, isUserTyping]);



    return (
        <section className="askai-root">
            {viewMode === "headsetPanel" ? (
                <AskHuman onBack={() => setViewMode("list")} />
            ) : (
                <>
                    {/* Workspace */}
                    <div className="askai-workspace">

                        {/* Mobile sidebar backdrop */}
                        {isMobileSidebarOpen && (
                            <div
                                className="askai-sidebar-backdrop visible"
                                onClick={() => setIsMobileSidebarOpen(false)}
                            />
                        )}

                        {/* Sidebar Navigation */}
                        <aside className={`askai-sidebar ${sidebarCollapsed ? "collapsed" : ""} ${isMobileSidebarOpen ? "mobile-open" : ""}`}>
                            {/* Glass Container for Logo and Toggle Button */}
                            <div
                                style={{
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(16px)',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    padding: '16px',
                                    marginBottom: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: sidebarCollapsed ? 'center' : 'space-between',
                                    gap: '12px',
                                    position: 'relative'
                                }}
                            >
                                {/* Logo - clickable to open sidebar when collapsed */}
                                <div 
                                    style={{ 
                                        display: "flex", 
                                        alignItems: "center", 
                                        gap: "12px",
                                        cursor: sidebarCollapsed ? 'pointer' : 'default'
                                    }}
                                    onClick={() => {
                                        if (sidebarCollapsed) {
                                            setSidebarCollapsed(false);
                                        }
                                    }}
                                >
                                    <img src={auriImg} alt="Auri" style={{ height: "40px", width: "40px" }} />
                                    {!sidebarCollapsed && (
                                        <h1 style={{ 
                                            fontSize: "24px", 
                                            margin: "0",
                                            color: "#ffffff"
                                        }}>ASK AURI</h1>
                                    )}
                                </div>

                                {/* Toggle Button - only visible when sidebar is open */}
                                {!sidebarCollapsed && (
                                    <button
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '12px',
                                            background: 'transparent',
                                            color: '#ffffff',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '20px',
                                            transition: 'background 0.2s ease'
                                        }}
                                        onClick={() => {
                                            // On mobile, close the drawer instead of collapsing
                                            if (window.innerWidth < 768) {
                                                setIsMobileSidebarOpen(false);
                                            } else {
                                                setSidebarCollapsed(true);
                                            }
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        <FiMenu />
                                    </button>
                                )}
                            </div>

                            
                            {/* Search Bar */}
                            {!sidebarCollapsed && (
                                <div className="askai-search-box">
                                    <div className="askai-search-box-inner">
                                        <FiSearch className="askai-search-icon" />
                                        <input
                                            type="text"
                                            placeholder={t.searchChats}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {/* New Chat Button */}
                            {activeTab === "open" && (
                                <button
                                    className="askai-new-chat-btn"
                                    onClick={() => {
                                        setSelectedChat(null);
                                        setActiveChatId(null);
                                        setStarted(false);
                                        setDisclaimerConfirmed(false);
                                        setFinalMessageSent(false);
                                        messageOrderRef.current = 1;
                                        setNextMessageOrder(1);
                                        setMessages([
                                            {
                                                sender: "auri",
                                                text: t.heyThereImAuri,
                                            },
                                            {
                                                sender: "auri",
                                                text: t.beforeWeGetStarted,
                                            },
                                        ]);
                                        setNewMessage("");
                                        setIsUserTyping(false);
                                        setIsAuriTyping(false);
                                    }}
                                >
                                    <TbMessagePlus /> {!sidebarCollapsed && t.newChat}
                                </button>
                            )}
                            
                            {/* Open/Archived Tabs in Sidebar */}
                            <div className="askai-sidebar-tabs">
                                <button
                                    className={`askai-sidebar-tab-btn ${activeTab === "open" ? "active" : ""}`}
                                    onClick={() => handleTabChange("open")}
                                >
                                    <TbMessages /> {!sidebarCollapsed && t.open}
                                </button>
                                <button
                                    className={`askai-sidebar-tab-btn ${activeTab === "archived" ? "active" : ""}`}
                                    onClick={() => handleTabChange("archived")}
                                >
                                    <FiArchive /> {!sidebarCollapsed && t.archived}
                                </button>
                            </div>
                            
                            {/* Chat History */}
                            <div className="askai-chat-history">
                                {!sidebarCollapsed && <h3>Chats</h3>}
                                <div className="askai-chat-list">
                                    {chatsLoading && !chatsLoaded ? (
                                        // Skeleton loading for chat list
                                        [...Array(5)].map((_, i) => (
                                            <div key={i} className="askai-chat-item skeleton-chat-item">
                                                <div className="askai-chat-item-content">
                                                    <div className="skeleton skeleton-chat-title"></div>
                                                    <div className="skeleton skeleton-chat-time"></div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        filteredChats
                                            .filter(chat => 
                                                searchQuery === "" || 
                                                chat.title.toLowerCase().includes(searchQuery.toLowerCase())
                                            )
                                            .map((chat, i) => (
                                        <div
                                            key={chat.id || i}
                                            className={`askai-chat-item ${selectedChat?.id === chat.id ? "active" : ""}`}
                                            onClick={() => {
                                                setSelectedChat(chat);
                                                setActiveChatId(chat.id);
                                                setStarted(true);
                                                setFinalMessageSent(true);
                                                setDisclaimerConfirmed(true);
                                                setNewMessage("");
                                                setIsUserTyping(false);
                                                setIsAuriTyping(false);
                                                // Close mobile drawer when chat is selected
                                                setIsMobileSidebarOpen(false);
                                            }}
                                            title={chat.title}
                                            style={{ cursor: "pointer" }}
                                        >
                                            {!sidebarCollapsed && (
                                                <div className="askai-chat-item-content">
                                                    <div className="askai-chat-item-title">{chat.title.length > 50 ? chat.title.substring(0, 50) + '...' : chat.title}</div>
                                                    <div className="askai-chat-item-time">{chat.time}</div>
                                                </div>
                                            )}
                                            {!sidebarCollapsed && activeTab === "open" && chat.status === "active" && (
                                                <>
                                                    <button
                                                        className="askai-chat-item-archive"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleArchiveChat(e, chat);
                                                        }}
                                                        title="Archive chat"
                                                    >
                                                        <FiArchive />
                                                    </button>
                                                    <button
                                                        className="askai-chat-item-delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteChat(e, chat);
                                                        }}
                                                        title="Delete chat"
                                                    >
                                                        <FiTrash2 />
                                                    </button>
                                                </>
                                            )}
                                            {!sidebarCollapsed && activeTab === "archived" && chat.status === "archived" && (
                                                <>
                                                    <button
                                                        className="askai-chat-item-open"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenChat(e, chat);
                                                        }}
                                                        title="Open chat"
                                                    >
                                                        <FiExternalLink />
                                                    </button>
                                                    <button
                                                        className="askai-chat-item-delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteChat(e, chat);
                                                        }}
                                                        title="Delete chat"
                                                    >
                                                        <FiTrash2 />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    ))
                                    )}
                                </div>
                            </div>
                        </aside>

                        {/* Main Content */}
                        <main className="askai-main">
                            <div className="conversation-panel">
                                {/* Mobile hamburger bar — visible only on mobile via CSS */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#fff' }} className="askai-mobile-topbar">
                                    <button
                                        className="askai-mobile-sidebar-btn"
                                        onClick={() => setIsMobileSidebarOpen(true)}
                                        aria-label="Open chat history"
                                        aria-expanded={isMobileSidebarOpen}
                                    >
                                        <FiMenu />
                                    </button>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <img src={auriImg} alt="Auri" style={{ height: '28px', width: '28px', objectFit: 'contain' }} />
                                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>Ask Auri</span>
                                    </div>
                                </div>
                                {/* Scrollable messages */}
                                <div className="messages-scroll" ref={messagesScrollRef}>
                                    {messagesLoading && !messagesLoaded ? (
                                        // Skeleton loading for conversation panel
                                        [...Array(3)].map((_, i) => (
                                            <div key={i} className="chat-message skeleton-message">
                                                <div className="skeleton skeleton-message-avatar"></div>
                                                <div className="skeleton skeleton-message-text"></div>
                                                <div className="skeleton skeleton-message-text"></div>
                                                <div className="skeleton skeleton-message-text" style={{ width: '60%' }}></div>
                                            </div>
                                        ))
                                    ) : (
                                        messages.map((msg, i) => {
                                            console.log('Rendering message:', { sender: msg.sender, hasSourceUrl: !!msg.sourceUrl, sourceUrl: msg.sourceUrl });
                                            return (
                                            <div
                                                key={i}
                                                className={`chat-message ${msg.sender === "user" ? "user" : "auri"}`}
                                            >
                                                {(msg.sender === "auri" || msg.sender === "assistant") && <img src={auriImg} alt="Auri" />}
                                                <div style={{ display: "flex", flexDirection: "column", maxWidth: "100%" }}>
                                                    <div className="message-text">
                                                        {msg.text}
                                                        {(msg.sender === "auri" || msg.sender === "assistant") && msg.sourceUrl && (
                                                            <>
                                                                <br />
                                                                <a
                                                                    href={msg.sourceUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{
                                                                        display: "inline-block",
                                                                        marginTop: "4px",
                                                                        fontSize: "12px",
                                                                        color: "#24459B",
                                                                        textDecoration: "underline",
                                                                        fontWeight: "500",
                                                                        opacity: 0.9,
                                                                    }}
                                                                >
                                                                    Read more ↗
                                                                </a>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            );
                                        })
                                    )}



                                    {isUserTyping && (
                                        <div className="chat-message user typing">
                                            <div className="message-text typing-text">
                                                <span className="typing-dot">.</span>
                                                <span className="typing-dot">.</span>
                                                <span className="typing-dot">.</span>
                                            </div>
                                        </div>
                                    )}

                                    {isAuriTyping && (
                                        <div className="chat-message auri typing">
                                            <img src={auriImg} alt="Auri" />
                                            <div className="message-text typing-text">
                                                <span className="typing-dot">.</span>
                                                <span className="typing-dot">.</span>
                                                <span className="typing-dot">.</span>
                                            </div>
                                        </div>
                                    )}

                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Input area (fixed, not scrollable) */}
                                <div className="chat-input-area">
                                    <button
                                        className="headset-btn"
                                        onClick={() => setViewMode("headsetPanel")}
                                        title="Human Support"
                                        disabled={selectedChat && selectedChat.status === "archived"}
                                        style={{
                                            opacity: (selectedChat && selectedChat.status === "archived") ? 0.5 : 1,
                                            cursor: (selectedChat && selectedChat.status === "archived") ? "not-allowed" : "pointer"
                                        }}
                                    >
                                        <FaHeadset size={20} />
                                    </button>

                                    {!selectedChat && !disclaimerConfirmed && (
                                        <button
                                            className="lets-started-btn-full"
                                            onClick={handleIUnderstand}
                                        >
                                            {t.iUnderstand}
                                        </button>
                                    )}

                                    {!selectedChat && disclaimerConfirmed && started && !finalMessageSent && (
                                        <button
                                            className="lets-started-btn-full"
                                            onClick={handleLetsGetStarted}
                                        >
                                            {t.letsGetStarted}
                                        </button>
                                    )}

                                    {(!selectedChat && !disclaimerConfirmed) || (!selectedChat && disclaimerConfirmed && started && !finalMessageSent) ? null : (
                                        <>
                                            <textarea
                                                ref={textareaRef}
                                                placeholder={t.typeYourMessage}
                                                value={newMessage}
                                                onChange={(e) => handleTyping(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSendMessage();
                                                    }
                                                }}
                                                disabled={!finalMessageSent || (selectedChat && selectedChat.status === "archived")}
                                                style={{ cursor: (!finalMessageSent || (selectedChat && selectedChat.status === "archived")) ? "not-allowed" : "text" }}
                                                rows={1}
                                            />

                                            <button
                                                className="send-btn"
                                                onClick={handleSendMessage}
                                                disabled={!finalMessageSent || (selectedChat && selectedChat.status === "archived")}
                                                style={{ opacity: (!finalMessageSent || (selectedChat && selectedChat.status === "archived")) ? 0.5 : 1 }}
                                            >
                                                <FiArrowUp />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </main>
                    </div>
                </>
            )}

            {/* Archive Confirmation Modal */}
            {showArchiveModal && (
                <div className="modal-overlay">
                    <div className="modal-content archive-modal">
                        <div className="archive-modal-header">
                            <h3>Archive Chat</h3>
                        </div>
                        <div className="archive-modal-body">
                            <p className="modal-description">This chat will be moved to your archived chats and will no longer appear in your active chats list.</p>
                            <p>Are you sure you want to archive "{chatToArchive?.title}"?</p>
                        </div>
                        <div className="archive-modal-footer">
                            <button className="modal-btn cancel" onClick={cancelArchiveChat}>
                                Cancel
                            </button>
                            <button className="modal-btn confirm archive-btn" onClick={confirmArchiveChat}>
                                Archive
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-content delete-modal">
                        <div className="delete-modal-header">
                            <h3>Delete Chat</h3>
                        </div>
                        <div className="delete-modal-body">
                            <p className="modal-description">This action cannot be undone. The chat and all its messages will be permanently deleted.</p>
                            <p>Are you sure you want to delete "{chatToDelete?.title}"?</p>
                        </div>
                        <div className="delete-modal-footer">
                            <button className="modal-btn cancel" onClick={cancelDeleteChat}>
                                Cancel
                            </button>
                            <button className="modal-btn confirm delete-btn" onClick={confirmDeleteChat}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Open Chat Confirmation Modal */}
            {showOpenModal && (
                <div className="modal-overlay">
                    <div className="modal-content open-modal">
                        <div className="open-modal-header">
                            <h3>Open Chat</h3>
                        </div>
                        <div className="open-modal-body">
                            <p className="modal-description">This chat will be moved back to your active chats list.</p>
                            <p>Are you sure you want to open "{chatToOpen?.title}"?</p>
                        </div>
                        <div className="open-modal-footer">
                            <button className="modal-btn cancel" onClick={cancelOpenChat}>
                                Cancel
                            </button>
                            <button className="modal-btn confirm open-btn" onClick={confirmOpenChat}>
                                Open
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Profanity Modal */}
            <ProfanityModal 
                isOpen={showProfanityModal} 
                onClose={() => setShowProfanityModal(false)} 
            />
        </section>
    );
}